import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiService, ServiceType } from "./apiServices";
import { getAuth0Token } from '../auth/utils';
import { TRAVELLER_V3_ENDPOINTS } from './apiEndPoints';
import { travellerKeys } from './travellerApi2';
import { userV3Keys } from './userApi3';
import { useLocalization } from '../shared/contexts/LocalizationContext';

// Types
export interface CreateItineraryWithCategoryRequest {
  user_id: string;
  name: string;
  flight_number: string;
  departure_airport_id: number;
  destination_airport_id: number;
  corridor_id: string;
  departure_date: string;
  departure_time: string;
  arrival_date: string;
  arrival_time: string;
  weight: number;
  type: string;
  price: number;
  drop_of_date: string;
  drop_of_time: string;
  pick_up_date: string;
  pick_up_time: string;
  sender_drop_off_address: string;
  receiver_drop_off_address: string;
  time_zone?: string;
  itineraries: Array<{
    category_id: string;
  }>;
}

export interface UpdateItineraryWithCategoryRequest {
  itinerary_id: string;
  user_id: string;
  name: string;
  flight_number: string;
  departure_airport_id: number;
  destination_airport_id: number;
  corridor_id: string;
  departure_date: string;
  departure_time: string;
  arrival_date: string;
  arrival_time: string;
  weight: number;
  type: string;
  price: number;
  drop_of_date: string;
  drop_of_time: string;
  pick_up_date: string;
  pick_up_time: string;
  sender_drop_off_address: string;
  receiver_drop_off_address: string;
  time_zone?: string;
  itineraries: Array<{
    category_id: string;
  }>;
}

// Traveler sends offer request to sender
export interface SendOfferRequest {
  shipment_id: string;
  itinerary_id: string;
  expiration_duration?: number;
  allocated_price: number;
  sender_platform_fee_percentage: number;
  traveler_platform_fee_percentage: number;
  sender_platform_fee: number;
  traveler_platform_fee: number;
}

// Traveler accepts sender's offer request
export interface AcceptOfferRequestByTraveler {
  order_code: string;
}

// Traveler rejects sender's offer request
export interface RejectOfferRequestByTraveler {
  order_code: string;
  reason?: string;
}

export interface ItineraryOrder {
  order_id: string;
  orderCode: string;
  name: string;
  profileImageUrl: string;
  rating: number;
  reviews: number;
  trips: number;
  departureAirport: string;
  destinationAirport: string;
  departureAirportCode?: string;
  destinationAirportCode?: string;
  invNumber: string;
  status: string;
  allocatedWeight: number;
  allocatedPrice: number;
  traveler_platform_fee?: number;
  sender_id?: string;
  chat_room_id?: string | null;
  unread_messages_count?: number;
  group_chat_room_id?: string | null;
  group_chat_unread_messages_count?: number;
  approved_at?: string;
  checked_in_by_sender?: boolean;
  checked_in_by_traveller?: boolean;
  checked_out_by_traveller?: boolean;
  is_checked_out_by_receiver?: boolean;
  created_at?: string;
  cancellation_reason?: string;
  cancelled_at?: string;
  request_type?: string;
}

export interface OrderDetails {
  name: string;
  email: string;
  contact: string;
  profileImage: string;
  rating: number;
  sender_id: string;
  chat_room_id?: string | null;
  shipment_name: string;
  receiver_name: string;
  receiver_email: string;
  receiver_contact: string;
  from: string;
  to: string;
  orderCode: string;
  inv_number: string;
  allocated_weight: number;
  allocated_price: number;
  traveler_platform_fee: number;
  base_currency?: string;
  display_currency?: string;
  display_allocated_price?: string;
  display_traveler_platform_fee?: string;
  shipment_total_weight?: string;
  shipment_total_price?: string;
  display_shipment_total_price?: string;
  itinerary?: {
    id: string;
    weight: string;
    price: string;
    base_currency?: string;
    display_price?: string;
    display_currency?: string;
  };
  status: string;
  checked_in_by_sender: boolean;
  checked_in_by_traveller: boolean;
  checked_out_by_traveller: boolean;
  is_checked_out_by_receiver: boolean;
  created_at?: string;
  drop_of_date?: string;
  drop_of_time?: string;
  approved_at?: string;
  cancellation_reason?: string | null;
  cancelled_at?: string | null;
  departure_date?: string;
  departure_time?: string;
  pick_up_date?: string;
  pick_up_time?: string;
  arrival_date?: string;
  arrival_time?: string;
  items: Array<{
    id: string;
    category: string;
    price: number;
    pieces: number;
    pages: number;
    weight: number;
    images: string[];
    edit_price: number;
    edit_pieces: number;
    edit_pages: number;
    edit_weight: number;
    order_id: string;
    status: string | null;
  }>;
}

export interface OrderHistoryItem {
  order_id: string;
  orderCode: string;
  name: string;
  profileImageUrl: string;
  sender_id?: string;
  chat_room_id?: string | null;
  unread_messages_count?: number;
  group_chat_room_id?: string | null;
  group_chat_unread_messages_count?: number;
  rating: number;
  reviews: number;
  trips: number;
  departureAirport: string;
  destinationAirport: string;
  invNumber: string;
  status: string;
  allocatedWeight: number;
  allocatedPrice: number;
  traveler_platform_fee?: number;
  payment_status?: string;
  created_at?: string;
  approved_at?: string;
  cancellation_reason?: string;
  cancelled_at?: string;
  request_type?: string;
  departure_date?: string;
  arrival_date?: string;
}

export interface OrderHistoryResponse {
  filter?: string;
  myOrders: OrderHistoryItem[];
}

// Search senders based on trip details (authenticated)
export interface SearchSendersRequest {
  traveler_id: string;
  fromLocation: number;
  toLocation?: number;
  startDate: string;
  endDate: string;
  latitude: number;
  longitude: number;
  radius: number;
  min_weight?: number;
  max_weight?: number;
  page: number;
  limit: number;
  sortBy?: string;
}

// Search senders based on trip details (public, unauthenticated)
export interface SearchPublicSendersRequest {
  fromLocation: number;
  toLocation?: number;
  startDate: string;
  endDate: string;
  latitude: number;
  longitude: number;
  radius: number;
  min_weight?: number;
  max_weight?: number;
  page: number;
  limit: number;
  sortBy?: string;
  currency_code?: string;
}

export interface ItineraryDetails {
  id: string;
  itinerary_name: string;
  name: string;
  email: string;
  contact_number: string;
  profileImageUrl: string;
  user_id: string;
  rating: string;
  reviews: number;
  trips: number;
  confirmation_number?: string | null;
  flight_number?: string;
  departure_airport: string;
  destination_airport: string;
  departure_airport_code?: string;
  destination_airport_code?: string;
  departureAirportId?: number;
  destinationAirportId?: number;
  departure_date: string;
  departure_time: string;
  arrival_date: string;
  arrival_time: string;
  type: string;
  weight: string;
  price: string;
  drop_of_date?: string;
  drop_of_time?: string;
  pick_up_date?: string;
  pick_up_time?: string;
  ticket_image?: string;
  passport_image?: string;
  drop_of_address?: string;
  pick_up_address?: string;
  created_at?: string;
  time_zone?: string;
  is_editable: boolean;
  categories: Array<{
    category_id: string;
    category_name: string;
  }>;
  remaining_luggage_capacity?: {
    current_item_weight: string;
    remaining_weight: string;
    total_weight_limit: string;
  };
  travel_earnings_summary?: {
    total_orders: number;
    total_gross_earnings: number;
    total_platform_fee: number;
    estimated_payout: number;
    base_currency?: string;
    display_currency?: string;
    display_total_gross_earnings?: number;
    display_total_platform_fee?: number;
    display_estimated_payout?: number;
  };
  base_currency?: string;
  display_price?: string;
  display_currency?: string;
}

// Get 1:1 and group chat room ids + unread counts for an order
export interface OrderChatInfoResponse {
  chat_room_id: string | null;
  unread_messages_count: number;
  group_chat_room_id: string | null;
  group_chat_unread_messages_count: number;
}

// Add rating and review for a sender
export interface AddSenderRatingReviewRequest {
  user_id: string;
  reviewer_id: string;
  order_id: string;
  rating: number;
  reason: string;
  comment: string;
}

export interface RatingReviewResponse {
  message: string;
  data: unknown;
}

// Get reviews and ratings for a user
export interface UserReview {
  id: string;
  reviewer_id: string;
  reviewer_name: string;
  reviewer_profile_image: string | null;
  reviewer_role: string;
  rating: number;
  reason: string;
  comment: string;
  order_id: string;
  created_at: string;
}

export interface GetReviewsResponse {
  reviews: UserReview[];
  average_rating: number;
  total_reviews: number;
}

export interface WalletTransaction {
  id: string;
  wallet_id: string;
  order_id: string;
  order_code: string;
  type: 'credit' | 'debit';
  amount: string;
  transaction_type: string;
  description: string;
  created_at: string;
}

export interface WalletBalanceTransactionsResponse {
  wallet: {
    balance: string;
  };
  transactions: WalletTransaction[];
}

// API functions
const travellerApiV3Client = {
  // Base query function
  async request({ url, method = 'GET', body }: { url: string; method?: string; body?: unknown }) {
    try {
      const travellerService = getApiService(ServiceType.traveller3);
      const token = getAuth0Token();

      if (token) {
        travellerService.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }

      const response = await travellerService({
        url,
        method,
        data: body,
      });
      return response.data;
    } catch (error: unknown) {
      const err = error as {
        response?: {
          status?: number;
          data?: unknown;
        };
        message?: string;
      };
      throw {
        status: err.response?.status,
        data: err.response?.data || err.message,
      };
    }
  },

  // Create a travel itinerary with category
  createItinerary: (body: CreateItineraryWithCategoryRequest) =>
    travellerApiV3Client.request({
      url: TRAVELLER_V3_ENDPOINTS.CREATE_ITINERARY.path,
      method: 'POST',
      body,
    }),

  // Update a travel itinerary with category
  updateItinerary: (body: UpdateItineraryWithCategoryRequest) => {
    const { itinerary_id, ...rest } = body;
    return travellerApiV3Client.request({
      url: TRAVELLER_V3_ENDPOINTS.UPDATE_ITINERARY.path.replace(':itinerary_id', String(itinerary_id)),
      method: 'PUT',
      body: rest,
    });
  },

  // List itineraries with pagination
  listItineraries: (userId: string, page: number, limit: number, sort: string) =>
    travellerApiV3Client.request({
      url: `${TRAVELLER_V3_ENDPOINTS.LIST_ITINERARY.path.replace(':userId', userId)}?page=${page}&limit=${limit}&sort=${sort}`,
    }),

  // Get wallet balance and transactions
  getWalletBalanceTransactions: (userId: string, startDate?: string, endDate?: string) => {
    let url = TRAVELLER_V3_ENDPOINTS.WALLET_BALANCE_TRANSACTIONS.path.replace(':userId', userId);
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    return travellerApiV3Client.request({
      url,
      method: 'GET',
    });
  },

  // Search itineraries by name for a traveler
  searchItineraries: (traveler_id: string, name?: string) => {
    const params = new URLSearchParams();
    if (name) params.append('name', name);
    const queryParams = params.toString() ? `?${params.toString()}` : '';
    return travellerApiV3Client.request({
      url: TRAVELLER_V3_ENDPOINTS.CHECK_ITINERARIES.path.replace(':traveler_id', traveler_id) + queryParams,
      method: 'GET',
    });
  },

  // Traveler sends offer request by providing shipment and itinerary details
  sendOfferRequest: (body: SendOfferRequest) =>
    travellerApiV3Client.request({
      url: TRAVELLER_V3_ENDPOINTS.SEND_OFFER_REQUEST.path,
      method: 'POST',
      body,
    }),

  // Traveler accepts a sender's offer request by order code
  acceptOfferRequest: (body: AcceptOfferRequestByTraveler) =>
    travellerApiV3Client.request({
      url: TRAVELLER_V3_ENDPOINTS.ACCEPT_OFFER_REQUEST.path,
      method: 'POST',
      body,
    }),

  // Traveler rejects a sender's offer request by order code
  rejectOfferRequest: (body: RejectOfferRequestByTraveler) =>
    travellerApiV3Client.request({
      url: TRAVELLER_V3_ENDPOINTS.REJECT_OFFER_REQUEST.path,
      method: 'POST',
      body,
    }),

  // Get pending requests by itinerary_id
  getPendingRequestsByItinerary: (itinerary_id: string) =>
    travellerApiV3Client.request({
      url: TRAVELLER_V3_ENDPOINTS.GET_PENDING_REQUESTS_BY_ITINERARY.path.replace(':itinerary_id', itinerary_id),
      method: 'GET',
    }),

  // Get my pending requests by itinerary_id (status = 'pending')
  getMyPendingRequestsByItinerary: (itinerary_id: string) =>
    travellerApiV3Client.request({
      url: TRAVELLER_V3_ENDPOINTS.GET_MY_PENDING_REQUESTS_BY_ITINERARY.path.replace(':itinerary_id', itinerary_id),
      method: 'GET',
    }),

  // Get itinerary details (orders, airports, categories, and related data)
  getItineraryDetails: (itinerary_id: string) =>
    travellerApiV3Client.request({
      url: TRAVELLER_V3_ENDPOINTS.ITINERARY_DETAILS.path.replace(':itinerary_id', itinerary_id),
      method: 'GET',
    }),

  // Get itinerary orders by itinerary_id
  getItineraryOrders: (itinerary_id: string) =>
    travellerApiV3Client.request({
      url: TRAVELLER_V3_ENDPOINTS.ITINERARY_ORDERS.path.replace(':itinerary_id', itinerary_id),
      method: 'GET',
    }),

  // Get order details by order_code
  getOrderDetails: (order_code: string) =>
    travellerApiV3Client.request({
      url: TRAVELLER_V3_ENDPOINTS.ORDER_DETAILS.path,
      method: 'POST',
      body: { order_code },
    }),

  // Get traveler orders (all, in_progress, completed, cancelled, under_review, closed)
  getOrderHistory: (traveler_id: string, filter: string = 'all') => {
    const params = new URLSearchParams();
    if (filter && filter !== 'all') {
      params.append('filter', filter);
    }
    const queryParams = params.toString() ? `?${params.toString()}` : '';
    return travellerApiV3Client.request({
      url: TRAVELLER_V3_ENDPOINTS.ORDER_HISTORY.path.replace(':traveler_id', traveler_id) + queryParams,
      method: 'GET',
    });
  },

  // Search senders based on trip details (authenticated)
  searchSenders: (body: SearchSendersRequest) =>
    travellerApiV3Client.request({
      url: TRAVELLER_V3_ENDPOINTS.SEARCH_TRIPS.path,
      method: 'POST',
      body,
    }),

  // Search senders based on trip details (public, no auth required)
  searchPublicSenders: (body: SearchPublicSendersRequest) =>
    travellerApiV3Client.request({
      url: TRAVELLER_V3_ENDPOINTS.PUBLIC_SENDER_SEARCH.path,
      method: 'POST',
      body,
    }),

  // Get 1:1 and group chat room ids + unread counts for an order
  getOrderChatInfo: (order_id: string, traveler_id: string): Promise<OrderChatInfoResponse> =>
    travellerApiV3Client.request({
      url: TRAVELLER_V3_ENDPOINTS.GET_ORDER_CHAT_INFO.path
        .replace(':order_id', order_id)
        .replace(':traveler_id', traveler_id),
      method: 'GET',
    }),

  // Dismiss the "rate your sender" prompt for an order
  skipRating: (order_id: string, traveler_id: string) =>
    travellerApiV3Client.request({
      url: TRAVELLER_V3_ENDPOINTS.SKIP_RATING.path
        .replace(':order_id', order_id)
        .replace(':traveler_id', traveler_id),
      method: 'POST',
    }),

  // Add rating and review for a sender
  addSenderRatingReview: (params: AddSenderRatingReviewRequest): Promise<RatingReviewResponse> =>
    travellerApiV3Client.request({
      url: TRAVELLER_V3_ENDPOINTS.ADD_SENDER_RATING_REVIEW.path,
      method: 'POST',
      body: params,
    }),

  // Get reviews and ratings for a user
  getReviews: (user_id: string, role: 'all' | 'sender' | 'traveler' = 'all'): Promise<GetReviewsResponse> => {
    const params = new URLSearchParams();
    params.append('role', role);
    return travellerApiV3Client.request({
      url: `${TRAVELLER_V3_ENDPOINTS.GET_REVIEWS.path.replace(':user_id', user_id)}?${params.toString()}`,
      method: 'GET',
    });
  },
};

// TanStack Query hooks

// Create itinerary with category mutation
export const useCreateItineraryMutation = () => {
  const queryClient = useQueryClient();
  const { timeZone } = useLocalization();

  return useMutation({
    mutationFn: (body: CreateItineraryWithCategoryRequest) =>
      travellerApiV3Client.createItinerary({ ...body, time_zone: timeZone }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: travellerKeys.itineraries(Number(variables.user_id)) });
      queryClient.invalidateQueries({ queryKey: travellerKeys.checkItineraries(Number(variables.user_id)) });
      queryClient.invalidateQueries({ queryKey: travellerKeys.allItineraries });
    },
  });
};

// Update itinerary with category mutation
export const useUpdateItineraryMutation = () => {
  const queryClient = useQueryClient();
  const { timeZone } = useLocalization();

  return useMutation({
    mutationFn: (body: UpdateItineraryWithCategoryRequest) =>
      travellerApiV3Client.updateItinerary({ ...body, time_zone: timeZone }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: travellerKeys.itineraries(Number(variables.user_id)) });
      queryClient.invalidateQueries({ queryKey: travellerKeys.checkItineraries(Number(variables.user_id)) });
      queryClient.invalidateQueries({ queryKey: travellerKeys.itineraryDetail(variables.itinerary_id) });
      queryClient.invalidateQueries({ queryKey: travellerKeys.allItineraries });
    },
  });
};

// List itineraries query hook
export const useListItinerariesQuery = (
  userId: string,
  page: number = 1,
  limit: number = 10,
  sort: string = 'created_at_desc',
  options = {}
) =>
  useQuery({
    queryKey: [...travellerKeys.itineraries(Number(userId)), page, limit, sort],
    queryFn: () => travellerApiV3Client.listItineraries(userId, page, limit, sort),
    ...options,
  });

// Get wallet balance and transactions query hook
export const useGetWalletBalanceTransactionsQuery = (
  userId: string,
  startDate?: string,
  endDate?: string,
  options = {}
) =>
  useQuery<WalletBalanceTransactionsResponse>({
    queryKey: travellerKeys.walletBalanceTransactions(userId, startDate, endDate),
    queryFn: () => travellerApiV3Client.getWalletBalanceTransactions(userId, startDate, endDate),
    enabled: !!userId,
    ...options,
  });

// Search itineraries by name query
export const useSearchItinerariesQuery = (traveler_id: string, name?: string, options = {}) =>
  useQuery({
    queryKey: ['travellerV3', 'searchItineraries', traveler_id, name] as const,
    queryFn: () => travellerApiV3Client.searchItineraries(traveler_id, name),
    enabled: !!traveler_id,
    ...options,
  });

// Traveler sends offer request mutation
export const useSendOfferRequestMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: SendOfferRequest) => travellerApiV3Client.sendOfferRequest(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: travellerKeys.allItineraries });
      queryClient.invalidateQueries({ queryKey: travellerKeys.allMyOrders });
    },
  });
};

// Traveler accepts sender's offer request mutation
export const useAcceptOfferRequestMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: AcceptOfferRequestByTraveler) => travellerApiV3Client.acceptOfferRequest(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: travellerKeys.allItineraries });
      queryClient.invalidateQueries({ queryKey: travellerKeys.allMyOrders });
    },
  });
};

// Traveler rejects sender's offer request mutation
export const useRejectOfferRequestMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: RejectOfferRequestByTraveler) => travellerApiV3Client.rejectOfferRequest(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: travellerKeys.allItineraries });
      queryClient.invalidateQueries({ queryKey: travellerKeys.allMyOrders });
    },
  });
};

// Get pending requests by itinerary ID query
export const useGetPendingRequestsByItineraryQuery = (itinerary_id: string, options = {}) =>
  useQuery({
    queryKey: ['travellerV3', 'pendingRequests', itinerary_id] as const,
    queryFn: () => travellerApiV3Client.getPendingRequestsByItinerary(itinerary_id),
    enabled: !!itinerary_id,
    ...options,
  });

// Get my pending requests by itinerary ID query (status = 'pending')
export const useGetMyPendingRequestsByItineraryQuery = (itinerary_id: string, options = {}) =>
  useQuery({
    queryKey: ['travellerV3', 'myPendingRequests', itinerary_id] as const,
    queryFn: () => travellerApiV3Client.getMyPendingRequestsByItinerary(itinerary_id),
    enabled: !!itinerary_id,
    ...options,
  });

// Get itinerary details query hook
export const useGetItineraryDetailsQuery = (itinerary_id: string, options = {}) =>
  useQuery<ItineraryDetails>({
    queryKey: travellerKeys.itineraryDetail(itinerary_id),
    queryFn: () => travellerApiV3Client.getItineraryDetails(itinerary_id),
    enabled: !!itinerary_id,
    ...options,
  });

// Get orders for a specific itinerary query hook
export const useGetItineraryOrdersQuery = (itinerary_id: string, options = {}) =>
  useQuery<{ itineraryOrders: ItineraryOrder[] }>({
    queryKey: travellerKeys.itineraryOrders(itinerary_id),
    queryFn: () => travellerApiV3Client.getItineraryOrders(itinerary_id),
    enabled: !!itinerary_id,
    ...options,
  });

// Get order details mutation hook (using mutation since it's a POST request)
export const useGetOrderDetailsMutation = () => {
  return useMutation<OrderDetails, Error, { order_code: string }>({
    mutationFn: ({ order_code }: { order_code: string }) =>
      travellerApiV3Client.getOrderDetails(order_code),
  });
};

// Get order history query hook
export const useGetOrderHistoryQuery = (
  traveler_id: string,
  filter: 'all' | 'in_progress' | 'completed' | 'cancelled' | 'under_review' | 'closed' = 'all',
  options = {}
) =>
  useQuery<OrderHistoryResponse>({
    queryKey: travellerKeys.orderHistory(traveler_id, filter),
    queryFn: () => travellerApiV3Client.getOrderHistory(traveler_id, filter),
    enabled: !!traveler_id,
    ...options,
  });

// Search senders based on trip details mutation (authenticated)
export const useSearchSendersMutation = () => {
  return useMutation({
    mutationFn: (body: SearchSendersRequest) => travellerApiV3Client.searchSenders(body),
  });
};

// Search senders based on trip details mutation (public, no auth required)
export const useSearchPublicSendersMutation = () => {
  return useMutation({
    mutationFn: (body: SearchPublicSendersRequest) => travellerApiV3Client.searchPublicSenders(body),
  });
};

// Get 1:1 and group chat room ids + unread counts for an order query
export const useGetOrderChatInfoQuery = (order_id: string, traveler_id: string, options = {}) =>
  useQuery<OrderChatInfoResponse>({
    queryKey: ['travellerV3', 'orderChatInfo', order_id, traveler_id] as const,
    queryFn: () => travellerApiV3Client.getOrderChatInfo(order_id, traveler_id),
    enabled: !!order_id && !!traveler_id,
    ...options,
  });

// Skip rating mutation
export const useSkipRatingMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ order_id, traveler_id }: { order_id: string; traveler_id: string }) =>
      travellerApiV3Client.skipRating(order_id, traveler_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: travellerKeys.allMyOrders });
    },
  });
};

// Add rating and review for a sender mutation
export const useAddSenderRatingReviewMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: AddSenderRatingReviewRequest) => travellerApiV3Client.addSenderRatingReview(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: travellerKeys.allMyOrders });
      // Refetch latest orders so is_rated_by_traveler updates and the rating prompt doesn't reappear
      queryClient.invalidateQueries({ queryKey: [...userV3Keys.all, 'latestOrders'] });
      queryClient.invalidateQueries({ queryKey: ['travellerV3', 'reviews'] });
    },
  });
};

// Get reviews and ratings for a user query
export const useGetReviewsQuery = (
  user_id: string,
  role: 'all' | 'sender' | 'traveler' = 'all',
  options = {}
) =>
  useQuery<GetReviewsResponse>({
    queryKey: ['travellerV3', 'reviews', user_id, role] as const,
    queryFn: () => travellerApiV3Client.getReviews(user_id, role),
    enabled: !!user_id,
    ...options,
  });
